/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, December 9th 2021, 10:49:41 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import React from "react";
import { UseFormReturn, useFormContext } from "react-hook-form";
import { schemaBuilder } from "lib/validation";
import { ObjectKey } from "lib/types";

/**
 * Context Provider for any kind of form, even a form that does not post to a onestop API endpoint.
 */

interface OSFormContextSpec<T> extends UseFormReturn<T> {
  fieldIsRequired: (schema_field_key: ObjectKey<T>) => boolean;
}

export const OSFormContext = React.createContext<OSFormContextSpec<any>>(null);

export const useOSFormContext = <T,>() => {
  return React.useContext(OSFormContext as React.Context<OSFormContextSpec<T>>);
};

// eslint-disable-next-line
export interface OSFormProviderProps<T> {
  validationSchema: schemaBuilder.AnyObjectSchema;
  children: React.ReactNode;
}

export const OSFormProvider = <T,>({ children, validationSchema }: OSFormProviderProps<T>) => {
  // Leaving validationSchema out of the dependency list because we are pretty sure the validationSchema
  // will never change for a OSForm unless someone decides to go crazy and implement a stateful
  // validation Schema for a form.
  const fieldIsRequired = React.useCallback(
    (schema_field_key: ObjectKey<T>) =>
      validationSchema.fields[schema_field_key]?.describe().tests.filter((v) => v.name === "required").length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const useFormReturn = useFormContext<T>();

  return <OSFormContext.Provider value={{ fieldIsRequired, ...useFormReturn }}>{children}</OSFormContext.Provider>;
};
